import { useEffect, useState } from 'react';
import axios from 'axios';
import logger from '../../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Compact 30-day matches sparkline shown inside the saved-views popover.
 * Fires once when the row is hovered (lazy load).
 */
const SavedViewSparkline = ({ viewId, eager = false, onClick }) => {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!eager || loaded) return;
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const { data: body } = await axios.get(
          `${API_URL}/api/saved-views/${viewId}/analytics?days=30`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!cancelled) setData(body);
      } catch (err) {
        logger.debug('sparkline fetch failed', err);
      } finally { setLoaded(true); }
    })();
    return () => { cancelled = true; };
  }, [eager, loaded, viewId]);

  if (!data || !data.buckets?.length) return null;

  const max = Math.max(1, ...data.buckets.map((b) => b.count));
  const w = 80;
  const h = 16;
  const step = w / (data.buckets.length - 1 || 1);
  const points = data.buckets
    .map((b, i) => `${(i * step).toFixed(1)},${(h - (b.count / max) * h).toFixed(1)}`)
    .join(' ');

  // Trend arrow (week-over-week)
  const trend = data.trend;
  let trendColor = 'text-muted-foreground';
  let arrow = '·';
  if (trend) {
    if (trend.direction === 'up') { trendColor = 'text-emerald-600'; arrow = '↑'; }
    else if (trend.direction === 'down') { trendColor = 'text-red-500'; arrow = '↓'; }
  }

  const Wrapper = onClick ? 'button' : 'span';
  const wrapperProps = onClick ? {
    type: 'button',
    onClick: (e) => { e.stopPropagation(); onClick(); },
    className: 'inline-flex items-center gap-1.5 text-[10px] text-muted-foreground hover:text-[#111565] cursor-pointer',
  } : { className: 'inline-flex items-center gap-1.5 text-[10px] text-muted-foreground' };

  return (
    <Wrapper
      {...wrapperProps}
      title={`${data.total} matches in last 30 days${trend ? ` · this week ${trend.recent} vs prior ${trend.prior}` : ''}${onClick ? ' — click for full chart' : ''}`}
      data-testid={`saved-view-sparkline-${viewId}`}
    >
      <svg width={w} height={h} className="overflow-visible">
        <polyline
          points={points}
          fill="none"
          stroke="#111565"
          strokeWidth="1.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <span>{data.total}/30d</span>
      {trend && (
        <span className={`font-semibold ${trendColor}`} data-testid={`saved-view-trend-${viewId}`}>
          {arrow}{trend.direction !== 'flat' ? `${Math.abs(trend.delta_pct)}%` : ''}
        </span>
      )}
    </Wrapper>
  );
};

export default SavedViewSparkline;
